import {
  AddressInput,
  Button,
  ButtonsWrapper,
  Form,
  PhoneNumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
  Separator,
  TextAreaInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';
import { SalutationsEnum } from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import NumberFormat from 'react-number-format';

import { useUpdateCustomer } from 'src/hooks/customers/update-customer.hook';
import { dateFormat } from 'src/common/helpers/date.helpers';
import { ICustomerData } from 'src/pages/customers/customers.interfaces';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { ContactPersonsList } from 'src/pages/customers/components/contact-persons-form-list';

import { FormElement, StyledTypography } from './customer-details-edit.styles';

const CustomerDetailsEdit = ({
  data,
  onCancelEdit,
}: {
  data: ICustomerData;
  onCancelEdit: () => void;
}) => {
  const { t } = useTranslation(['common', 'customers']);

  const { id } = useParams();
  const updateCustomer = useUpdateCustomer({ onSuccess: onCancelEdit });
  const currentCompanyId = useGetCurrentCompanyId();

  const onFinish = (values: ICustomerData) => {
    updateCustomer({
      variables: {
        payload: {
          ...values,
          _id: id,
          companyId: currentCompanyId,
        },
      },
    });
  };

  return (
    <Form layout="vertical" initialValues={data} onFinish={onFinish}>
      <FormElement>
        <div>
          <Form.Item name="salutation" label={t('customers:labels.salutation')}>
            <SelectInput defaultValue={data.salutation}>
              <SelectInput.Option value={SalutationsEnum.MR}>Mr</SelectInput.Option>
              <SelectInput.Option value={SalutationsEnum.MS}>Ms</SelectInput.Option>
              <SelectInput.Option value={SalutationsEnum.COMPANY}>Company</SelectInput.Option>
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const salutation = getFieldValue('salutation');

              return (
                <Form.Item
                  name="companyName"
                  label={t('customers:labels.companyName')}
                  rules={[
                    {
                      required: !salutation || salutation === SalutationsEnum.COMPANY,
                      message: t('settings:company.exceptions.requiredCompanyName'),
                    },
                  ]}
                >
                  <TextInput placeholder="Billinger" maxLength={50} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const salutation = getFieldValue('salutation');

              return (
                <Form.Item
                  name="customerName"
                  label={t('customers:labels.customerName')}
                  rules={[
                    {
                      required: salutation !== SalutationsEnum.COMPANY,
                      message: t('customers:error.customerNameRequired'),
                    },
                  ]}
                >
                  <TextInput placeholder="Savannah Nguyen" maxLength={50} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name="email"
            label={t('common:labels.email')}
            rules={[{ type: 'email', message: t('common:validation.error.incorrectFormat') }]}
          >
            <TextInput maxLength={50} placeholder="georgia.young@example.com" />
          </Form.Item>
        </div>
        <div>
          <Form.Item name="phoneNumber" label={t('common:labels.phoneNumber')}>
            <NumberFormat placeholder="9-13-123" format="#-##-###" customInput={TextInput} />
          </Form.Item>
          <Form.Item name="mobilePhoneNumber" label={t('customers:labels.mobilePhoneNumber')}>
            <PhoneNumberInput placeholder="+49 333 333 333333" />
          </Form.Item>
          <Form.Item label={t('customers:labels.dateOfAccountCreation')}>
            <TextInput disabled value={dateFormat(data.createdAt)} />
          </Form.Item>
          <Form.Item name="notes" label={t('customers:labels.notes')}>
            <TextAreaInput
              maxLength={500}
              showCount
              placeholder={t('customers:labels.notesPlaceholder')}
            />
          </Form.Item>
        </div>
      </FormElement>
      <Separator />
      <StyledTypography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('common:labels.businessAddress')}
      </StyledTypography>
      <FormElement>
        <AddressInput t={t} twoColumn parentField="address" notRequired />
      </FormElement>
      <Separator />
      <ContactPersonsList t={t} isEdit />
      <ButtonsWrapper marginTop={30}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onCancelEdit}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default CustomerDetailsEdit;
